/**
 * Deposit API Handler
 * 
 * Handles API calls for deposit processing.
 * Follows Single Responsibility Principle - only manages deposit API communication.
 * 
 * @package betbeast
 */

import { showCustomHint } from '../custom-hint.js';

export class DepositAPIHandler {
    constructor() {
        this.ajaxUrl = window.accountData?.ajaxUrl || '/wp-admin/admin-ajax.php';
        this.nonce = window.accountData?.nonce || '';
        
        if (!this.ajaxUrl || !this.nonce) {
            console.error('❌ AJAX URL or nonce not found. Check wp_localize_script setup.');
        }
    }
    
    /**
     * Process deposit and get payment URL
     * 
     * @param {Object} params Deposit parameters
     * @param {string} params.paymentMethod Payment method code (e.g., APCOPAY_V2_CARD)
     * @param {string} params.variantKey Variant id (CPT post id) for per-variant schema selection
     * @param {number} params.amount Deposit amount
     * @param {string} params.requestedBonusPlanId Optional bonus plan ID
     * @param {string} params.conversionId Optional conversion ID
     * @returns {Promise<Object>} Response with status, paymentId, and continueUrl
     */
    async processDeposit({ paymentMethod, variantKey = '', amount, requestedBonusPlanId = '', conversionId = '', extraFields = {} }) {
        if (!paymentMethod || !amount) {
            showCustomHint('Payment method and amount are required', 'error');
            throw new Error('Payment method and amount are required');
        }

        const formData = new FormData();
        formData.append('action', 'betbeast_process_deposit');
        formData.append('nonce', this.nonce);
        formData.append('payment_method', paymentMethod);
        if (variantKey) {
            formData.append('variant_key', variantKey);
        }
        formData.append('amount', amount);
        
        if (requestedBonusPlanId) {
            formData.append('bonus_plan_id', requestedBonusPlanId);
        }
        
        if (conversionId) {
            formData.append('conversion_id', conversionId);
        }

        // Append schema-defined extra fields as extra_fields[code]=value
        for (const [code, value] of Object.entries(extraFields)) {
            if (value !== '' && value !== null && value !== undefined) {
                formData.append(`extra_fields[${code}]`, value);
            }
        }
        
        try {
            const response = await fetch(this.ajaxUrl, {
                method: 'POST',
                credentials: 'same-origin',
                body: formData
            });
            
            const data = await response.json();
            
            if (!data.success) {
                const errorMsg = data.data?.message || data.message || 'Deposit processing failed';
                showCustomHint(errorMsg, 'error');
                throw new Error(errorMsg);
            }
            
            return data.data;
        } catch (error) {
            console.error('❌ Deposit API Error:', error);
            
            // Only show hint if it's a network/parse error without a message already shown
            if (error instanceof TypeError || error.name === 'SyntaxError') {
                showCustomHint(error.message || 'Network error occurred', 'error');
            }
            
            throw error;
        }
    }
}
